import { useApiQuery } from "@/hooks/useApiQuery";
import { ProducedArtifact } from "@/lib/artifact";
import { chatArtifactPanelStore } from "@/stores/chat-artifact-panel-store";
import { ArtifactUrlDTO } from "@shaarp/common";
import { useState } from "react";

/**
 * How long a URL is used before the panel renews it: well inside the hour
 * the API signs it for (`S3Service.getPresignedUrl`), so the one Print opens
 * is never dead.
 */
export const ARTIFACT_URL_REFRESH_MS = 45 * 60 * 1000;

/**
 * Presigned URLs for the Artifact the side panel shows, never older than
 * {@link ARTIFACT_URL_REFRESH_MS}.
 *
 * The URL a message read back from the database carries was signed when
 * the thread was read, and dies an hour later: a page left open past that
 * showed S3's "Request has expired" in the panel. So the panel asks the API
 * for its own, and renews it on a timer — and on the tab's return, since a
 * hidden tab skips the timer and the app turns `refetchOnWindowFocus` off.
 *
 * A version produced live is the exception: the API cannot sign it yet (the
 * document is attached to its message once the turn ends), and the URL the
 * stream carried is seconds old, so that one is shown until it is due.
 *
 * Two URLs, because they are used differently:
 * - `printUrl` is only ever a URL inside its renewal window: Print opens a
 *   tab synchronously, as a popup blocker refuses one opened after a
 *   request, so it must already be valid at click time — and is off while a
 *   due renewal is on its way.
 * - `frameUrl` stays the first valid one of this version: a page already
 *   loaded does not need its URL any more, and a new `src` would reload it
 *   and throw the reader back to the top. A new version (an edit rewrites the
 *   same stored page) does reload it.
 */
export function useArtifactUrl(
  conversationId: string,
  artifact: ProducedArtifact
): {
  readonly frameUrl: string | undefined;
  readonly printUrl: string | undefined;
  /** No frame URL yet, and one is on its way: the page placeholder, not an error. */
  readonly isPending: boolean;
} {
  // Each version arrives with its own URL; only told apart by it, never
  // loaded from it unless it was just received live.
  const version = artifact.url ?? "";
  const publishedAt = chatArtifactPanelStore.publishedAt(artifact);
  const { data, isStale, isFetching, isPending } = useApiQuery<ArtifactUrlDTO>(
    {
      key: [
        "conversation",
        conversationId,
        "artifact",
        artifact.artifactId,
        "url",
        version,
      ],
      path: `/conversation/${conversationId}/artifact/${artifact.artifactId}/url`,
      initialData:
        publishedAt !== undefined && artifact.url
          ? { url: artifact.url }
          : undefined,
      initialDataUpdatedAt: publishedAt,
      staleTime: ARTIFACT_URL_REFRESH_MS,
      refetchInterval: ARTIFACT_URL_REFRESH_MS,
      refetchOnWindowFocus: true,
    }
  );

  const validUrl = !isStale && data?.url ? data.url : undefined;
  const [frame, setFrame] = useState<{ version: string; url: string } | null>(
    null
  );
  if (validUrl && frame?.version !== version) {
    // Set during render rather than in an effect: the frame gets its URL in
    // the same paint the valid URL arrives in.
    setFrame({ version, url: validUrl });
  }
  const frameUrl =
    frame?.version === version ? frame.url : validUrl;

  return {
    frameUrl,
    printUrl: validUrl,
    isPending: !frameUrl && (isPending || isFetching),
  };
}
